import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';

type Role =
  | 'presidencia'
  | 'diretoria'
  | 'financeiro'
  | 'juridico'
  | 'atendimento'
  | 'homologacao'
  | 'colonial';

interface RBACContextType {
  hasAccess: (requiredRole: Role | Role[]) => boolean;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

export const useRBAC = () => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC deve ser usado dentro de um RBACProvider');
  }
  return context;
};

interface RBACProviderProps {
  children: React.ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const hasAccess = useMemo(
    () =>
      (requiredRole: Role | Role[]): boolean => {
        if (!user) return false;

        const userRoleLower = user.role.toLowerCase();

        // Presidência, Diretoria e Jurídico têm acesso total
        if (['presidencia', 'diretoria', 'juridico'].includes(userRoleLower)) {
          return true;
        }

        // Verifica se o usuário tem a função necessária
        if (Array.isArray(requiredRole)) {
          return requiredRole.map((role) => role.toLowerCase()).includes(userRoleLower);
        }

        return userRoleLower === requiredRole.toLowerCase();
      },
    [user]
  );

  const contextValue = useMemo(() => ({ hasAccess }), [hasAccess]);

  return <RBACContext.Provider value={contextValue}>{children}</RBACContext.Provider>;
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: Role | Role[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { hasAccess } = useRBAC();

  if (!hasAccess(requiredRole)) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)]">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Acesso Negado</h1>
        <p className="text-gray-600">Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  return <>{children}</>;
};
